<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
  } from 'vue';
  import { useRoute, useRouter } from 'vue-router/composables';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import V2MenuContextBridge from '@/components/v2/menu-import/V2MenuContextBridge.vue';
  import V2MenuFlow from '@/components/v2/menu-import/V2MenuFlow.vue';
  import V2MenuTopActions from '@/components/v2/menu-import/workspace/V2MenuTopActions.vue';
  import V2StoreLiveScreen from '@/components/v2/store-setup/live/V2StoreLiveScreen.vue';
  import V2SetupStepOrders from '@/components/v2/store-setup/orders/V2SetupStepOrders.vue';
  import V2SetupBottomBar from '@/components/v2/store-setup/shell/V2SetupBottomBar.vue';
  import V2SetupShell from '@/components/v2/store-setup/shell/V2SetupShell.vue';
  import V2SetupTopBar from '@/components/v2/store-setup/shell/V2SetupTopBar.vue';
  import { isMenuFlowHandle } from '@/composition/menu-import/menu-import-context';
  import { EMPTY_FLOW_BAR } from '@/composition/store-setup/setup-flow-screen';
  import type {
    IFlowBarState,
    IFlowScreenHandle,
  } from '@/composition/store-setup/setup-flow-screen';
  import { useSetupLeaveGuard } from '@/composition/store-setup/use-setup-leave-guard';
  import { useStoreSetupStatus } from '@/composition/store-setup/use-store-setup-status';
  import { useGtag } from '@/composition/use-gtag';
  import { useStableWindowSize } from '@/composition/use-stable-window-size';
  import { useUi } from '@/composition/use-ui';
  import { publishedToast } from '@/domain/menu-import/publish-dialog';
  import { resolveSetupScreen } from '@/domain/store-setup/setup-flow';
  import { AlertType } from '@/interfaces/IAlert';
  import type { IBranch } from '@/interfaces/IBranch';
  import type { IPublishResponse } from '@/interfaces/IMenuImport';
  import type { IRestaurant } from '@/interfaces/IRestaurant';
  import type { IUser } from '@/interfaces/IUser';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { ignoreNavigationFailure } from '@/utils/navigation.utils';

  /**
   * `/:restaurantId/setup` (plan §4.3 to §4.9, §4.12): the part of the flow
   * after the store exists, resumable on any device. The screen comes from
   * the store's setup state: the menu (source or workspace, decided by the
   * menu flow from the import session), «Pedidos», or the Live screen. Each
   * screen of the `v-if` chain binds the single bottom bar through its
   * exposed handle.
   */
  type TSetupPageScreen = 'loading' | 'error' | 'menu' | 'orders' | 'live';

  const WIDE_MIN_WIDTH = 1024;
  const PHONE_MAX_WIDTH = 768;
  /** Stepper position of each screen (§4.0); none on the Live screen. */
  const STEP_INDEX: Readonly<Record<TSetupPageScreen, number | null>> = {
    loading: null,
    error: null,
    menu: 2,
    orders: 3,
    live: null,
  };

  const props = withDefaults(
    defineProps<{
      restaurant: IRestaurant;
      branch?: IBranch | null;
      user: IUser;
    }>(),
    { branch: null }
  );
  const proxy = getCurrentInstance()!.proxy;
  const router = useRouter();
  const route = useRoute();
  const gtag = useGtag();
  const ui = useUi();
  const restaurantStore = useRestaurantStore();
  const { width } = useStableWindowSize();
  const setupStatus = useStoreSetupStatus(() => props.restaurant?.id ?? null);
  const status = setupStatus.status;

  const screen = ref<TSetupPageScreen>('loading');
  /** «Pedidos» opened from the dashboard checklist (`?step=orders`). */
  const checklistMode = ref(false);
  /** «Pedidos» right after the first publish («Su menú ya está publicado»). */
  const fromPublish = ref(false);
  /** The mounted screen of the `v-if` chain below (its exposed handle). */
  const activeScreen = ref<IFlowScreenHandle | null>(null);
  /** The page's own writes in flight (the menu skip). */
  const pending = ref(0);
  let resolveRun = 0;
  let alive = true;

  const wide = computed(() => width.value >= WIDE_MIN_WIDTH);
  const phone = computed(() => width.value < PHONE_MAX_WIDTH);
  const stepIndex = computed(() => STEP_INDEX[screen.value]);
  const screenKey = computed(
    () => `${screen.value}:${activeScreen.value?.innerKey ?? ''}`
  );
  /** The menu flow's handle while it is the active screen (its top-bar actions: save state, «Deshacer», «Seguir después»). */
  const menuFlow = computed(() =>
    isMenuFlowHandle(activeScreen.value) ? activeScreen.value : null
  );
  const bar = computed<IFlowBarState>(() =>
    activeScreen.value ? activeScreen.value.bar : EMPTY_FLOW_BAR
  );
  /** «Pedidos» saves the branch the setup state reports (the store's first active branch). */
  const orderingBranch = computed<IBranch | null>(() => {
    const branchId = status.value?.ordering?.branchId ?? null;
    if (branchId === null) return null;
    return (
      restaurantStore.branches?.find((b) => b.id === branchId) ??
      (props.branch?.id === branchId ? props.branch : null)
    );
  });

  useSetupLeaveGuard(
    () => pending.value > 0 || activeScreen.value?.busy === true
  );

  //#region Screens
  function blurActive() {
    const active = document.activeElement;
    if (active instanceof HTMLElement) active.blur();
  }

  /** §4.0: blur, let the keyboard go, then swap (the shell scrolls up and focuses the new `<h1>`). */
  async function show(next: TSetupPageScreen) {
    if (screen.value === next) return;
    blurActive();
    await nextTick();
    if (!alive) return;
    screen.value = next;
    // The menu flow reports its own screens (source, workspace).
    if (next === 'orders' || next === 'live')
      gtag.event('setup_step_viewed', { step: next, entry: 'setup' });
  }

  /**
   * §4.12: the screen from the setup state (a store created outside the flow,
   * or a finished setup, goes to its dashboard).
   */
  async function resolveScreen(options: { fromPublish?: boolean } = {}) {
    const restaurantId = props.restaurant?.id ?? null;
    const run = ++resolveRun;
    const status = await setupStatus.load();
    if (
      !alive ||
      run !== resolveRun ||
      (props.restaurant?.id ?? null) !== restaurantId
    )
      return;
    if (!status) {
      await show('error');
      return;
    }
    const resolution = resolveSetupScreen(status, null, route.query);
    if ('redirect' in resolution) {
      void router
        .replace(`/${props.restaurant.stringId}/dashboard`)
        .catch(ignoreNavigationFailure);
      return;
    }
    // «Pedidos» needs the branch it saves (a store whose only branch was switched off has none).
    if (resolution.screen === 'orders' && !orderingBranch.value) {
      await show('error');
      return;
    }
    checklistMode.value = resolution.checklistMode;
    fromPublish.value =
      options.fromPublish === true && resolution.screen === 'orders';
    await show(
      resolution.screen === 'orders' || resolution.screen === 'live'
        ? resolution.screen
        : 'menu'
    );
  }

  function retry() {
    screen.value = 'loading';
    void resolveScreen();
  }

  function exit() {
    void router
      .push(`/${props.restaurant.stringId}/dashboard`)
      .catch(ignoreNavigationFailure);
  }

  /** The first publish moves the setup to «Pedidos»; an update says what it did. */
  function onPublished(response: IPublishResponse) {
    if (response.sync)
      ui.showToast(
        AlertType.Success,
        String(proxy.$t(publishedToast(response).key))
      );
    void resolveScreen({ fromPublish: true });
  }

  /** Another device or tab published the menu: this one moves on to where the setup is now. */
  function onPublishedElsewhere() {
    ui.showToast(
      AlertType.Success,
      String(proxy.$t('menuImport.publishedToast'))
    );
    void resolveScreen({ fromPublish: true });
  }

  /** «Omitir por ahora» (already confirmed): the Live screen, no-menu variant. */
  async function onSkipped() {
    pending.value++;
    const skipped = await setupStatus.patch({ menuSkipped: true });
    if (!alive) return;
    pending.value--;
    if (skipped) await resolveScreen();
  }

  /**
   * «Pedidos» saved: from the checklist, back to the dashboard; in the flow,
   * the Live screen (the server moved the stage).
   */
  function onOrdersSaved() {
    if (checklistMode.value) {
      ui.showToast(
        AlertType.Success,
        String(proxy.$t('storeSetup.orders.saved'))
      );
      void router
        .push(`/${props.restaurant.stringId}/dashboard`)
        .catch(ignoreNavigationFailure);
      return;
    }
    void resolveScreen();
  }

  /** «Configurar después» (stage `live` already saved): the Live screen. */
  function onOrdersSkipped() {
    void resolveScreen();
  }

  function goDashboard() {
    void router
      .push(`/${props.restaurant.stringId}/dashboard`)
      .catch(ignoreNavigationFailure);
  }

  function loadMenu() {
    void router
      .push(`/${props.restaurant.stringId}/menu/import`)
      .catch(ignoreNavigationFailure);
  }
  //#endregion

  //#region Bottom bar
  function onPrimary() {
    void activeScreen.value?.primary();
  }

  function onSecondary() {
    void activeScreen.value?.secondary();
  }

  function onBack() {
    void activeScreen.value?.back();
  }
  //#endregion

  // Another store in the same page (only the `:restaurantId` changed).
  watch(
    () => props.restaurant?.id ?? null,
    (restaurantId, previousId) => {
      if (restaurantId === null || restaurantId === previousId) return;
      screen.value = 'loading';
      void resolveScreen();
    }
  );

  onMounted(() => {
    void resolveScreen();
  });

  onBeforeUnmount(() => {
    alive = false;
  });
</script>

<script lang="ts">
  import type { NavigationGuardNext, Route } from 'vue-router';
  import { confirmSetupLeave } from '@/composition/store-setup/use-setup-leave-guard';

  // vue-router 3 has no onBeforeRouteLeave composable: the in-component guard
  // is an option, and reaches the page's state through the leave guard's
  // module-scoped hook (registered by useSetupLeaveGuard above).
  export default {
    async beforeRouteLeave(to: Route, from: Route, next: NavigationGuardNext) {
      next((await confirmSetupLeave()) ? undefined : false);
    },
  };
</script>

<template>
  <V2SetupShell
    class="v2-store-setup-page"
    :wide="wide"
    :screen-key="screenKey"
  >
    <template #top>
      <V2SetupTopBar :title="restaurant.name" :step="stepIndex" @exit="exit">
        <template #actions>
          <V2MenuContextBridge
            v-if="menuFlow"
            :key="menuFlow.contextId"
            :context="menuFlow.context"
          >
            <V2MenuTopActions :wide="wide" @later="exit" />
          </V2MenuContextBridge>
        </template>
      </V2SetupTopBar>
    </template>
    <div v-if="screen === 'loading'" class="setup-loading" aria-busy="true">
      <PosUiSkeleton class="loading-title" />
      <PosUiSkeleton class="loading-line" />
      <PosUiSkeleton class="loading-block" />
    </div>
    <PosUiEmptyState
      v-else-if="screen === 'error'"
      class="setup-error"
      icon="alert-circle-outline"
      :title="$t('storeSetup.errors.generic')"
    >
      <template #action>
        <PosUiButton color="neutral" variant="outline" @click="retry">{{
          $t('storeSetup.location.retry')
        }}</PosUiButton>
      </template>
    </PosUiEmptyState>
    <V2MenuFlow
      v-else-if="screen === 'menu'"
      ref="activeScreen"
      entry="setup"
      :restaurant="restaurant"
      :wide="wide"
      :phone="phone"
      @published="onPublished"
      @published-elsewhere="onPublishedElsewhere"
      @skipped="onSkipped"
    />
    <V2SetupStepOrders
      v-else-if="screen === 'orders' && status && orderingBranch"
      ref="activeScreen"
      :restaurant="restaurant"
      :branch="orderingBranch"
      :status="status"
      :user="user"
      :from-publish="fromPublish"
      :checklist-mode="checklistMode"
      :wide="wide"
      @saved="onOrdersSaved"
      @skipped="onOrdersSkipped"
    />
    <V2StoreLiveScreen
      v-else-if="screen === 'live' && status"
      ref="activeScreen"
      :restaurant="restaurant"
      :status="status"
      @dashboard="goDashboard"
      @load-menu="loadMenu"
    />
    <template #bottom>
      <V2SetupBottomBar
        :state="bar"
        :wide="wide"
        @back="onBack"
        @left="onBack"
        @secondary="onSecondary"
        @primary="onPrimary"
      />
    </template>
  </V2SetupShell>
</template>

<style scoped lang="scss">
  .v2-store-setup-page {
    .setup-loading {
      @apply tw-mx-auto tw-flex tw-w-full tw-max-w-[54rem] tw-flex-col tw-gap-3;
      .loading-title {
        @apply tw-h-8 tw-w-2/3;
      }
      .loading-line {
        @apply tw-h-5 tw-w-full;
      }
      .loading-block {
        @apply tw-h-40 tw-w-full;
      }
    }
    .setup-error {
      @apply tw-flex-1;
    }
  }
</style>
